import React, { useEffect } from 'react'
import { getPublicPost } from '@fs/zion-feed-posts'
import { Switch, Route, NotFound, Redirect, useLocation, useQueryParams } from '@fs/zion-router'
import { trackEvent } from '@fs/zion-analytics'
import ErrorBoundary from '@fs/zion-error-boundary'
import { useExperiment } from '@fs/zion-experiments'
import { ChineseNLIHP } from '@fs/home-chinese-pages'
import { i18n } from '@fs/zion-locale'
import { useFeatureFlag } from '@fs/zion-flags'
import { useUser } from '@fs/zion-user'
import { BrightspotPage, BrightspotRouting } from '@fs/zion-cms'

const AboutPage = React.lazy(() => import('./pages/AboutPage/AboutPage'))
const SponsorshipPage = React.lazy(() => import('./pages/SponsorshipPage/SponsorshipPage'))
const BlankPage = React.lazy(() => import('./pages/BlankPage/Blank'))
const NLIHP = React.lazy(() => import('./pages/NLIHP/NLIHP'))
const PostRTBanner = React.lazy(() => import('./shared/components/PostRTBanner/PostRTBanner'))
const LIHP = React.lazy(() => import('./pages/LIHPProject/LIHP'))
const OrdinancesProject = React.lazy(() => import('./pages/OrdinancesProject'))
const SharablePage = React.lazy(() => import('./pages/LIHPProject/pages/SharablePage'))
const InviteFriendsSharePage = React.lazy(() => import('./pages/LIHPProject/pages/InviteFriendsSharePage'))
const SinglePostViewPage = React.lazy(() => import('./pages/LIHPProject/pages/SinglePostViewPage'))
const LoadingSpinner = React.lazy(() => import('./shared/components/LoadingSpinner'))

function App({ setUseFatFooter }) {
  const { signedIn, userLoading } = useUser()
  // The pathname must come from window.location.pathname and not from useLocation so that the basename will be included
  const pathname = window?.location?.pathname
  const { search, hash, pathname: locationPathname } = useLocation()
  const langRedirect =
    pathname && !pathname.match(/^\/([a-z]{2}|zh-hans|ceb)\//i) && !pathname.match(/^\/([a-z]{2}|zh-hans|ceb)$/i)
  const [postRtBannerNlihpCoreTenEx] = useExperiment('postRtBannerNlihpCoreTenEx')
  const { isOn: ordinancesPageFlagOn } = useFeatureFlag('home_shared_ordinancesPage')
  const { treatment: ordinancesABTest } = useFeatureFlag('home_shared_ordinancesAB')
  const { isOn: showFatFooter } = useFeatureFlag('home_showFatFooter')
  const ordinancesPageTest = ordinancesABTest === 'ordinancesPage' || ordinancesABTest === 'both'

  const homePath = '(home|home-react)?'
  const localePath = '([a-z]{2}|zh-hans|ceb)?'
  const matchRootPath = `/${localePath}/${homePath}`

  useEffect(() => {
    const fatFooterPages = (signedIn === false && locationPathname === '/') || locationPathname.includes('/about')
    if (!userLoading && fatFooterPages) {
      trackEvent({
        event_name: showFatFooter ? `Fat Footer Viewed` : `Minimum Footer Viewed`,
        link_page: locationPathname,
      })
    }
    if (showFatFooter && fatFooterPages) {
      setUseFatFooter(true)
    } else {
      setUseFatFooter(false)
    }
  }, [showFatFooter, signedIn, locationPathname, setUseFatFooter, userLoading])

  return (
    <ErrorBoundary>
      {postRtBannerNlihpCoreTenEx && <Route exact path="/(zh|zh-hans)/" render={() => <PostRTBanner />} />}
      <Switch>
        {langRedirect && <Redirect to={`/${i18n.language}${pathname}${search}${hash}`} />}

        <Route path={`${matchRootPath}/sharable/invite-friends`}>
          <SharablePage sharableComponent={InviteFriendsSharePage} />
        </Route>

        <Route path={`${matchRootPath}/about`} component={AboutPage} />
        <Route path={`${matchRootPath}/sponsorship`} component={SponsorshipPage} />
        <Route path={`${matchRootPath}/blank`} render={() => <BlankPage />} />
        <Route path={`${matchRootPath}/site-map`} render={() => <BrightspotPage />} />
        <Route path={`${matchRootPath}/pages`} render={() => <BrightspotRouting site="home" />} />

        {(ordinancesPageFlagOn || ordinancesPageTest) && (
          <Route path={`${matchRootPath}/ordinances`} render={() => <OrdinancesProject />} />
        )}

        <Route path={matchRootPath} exact>
          <HomePageRoutes matchRootPath={matchRootPath} />
        </Route>
        <Route path={`${matchRootPath}/(feed|friends|tasks|recents|to-do)`}>
          <HomePageRoutes matchRootPath={matchRootPath} />
        </Route>

        <Route component={NotFound} />
      </Switch>
    </ErrorBoundary>
  )
}

function HomePageRoutes() {
  const { query } = useQueryParams()
  const singlePost = Boolean(query.post)
  const { userLoading, signedIn } = useUser()
  const { pathname } = useLocation()
  if (userLoading) {
    return <LoadingSpinner />
  }
  if (!signedIn) {
    if (singlePost) {
      return <SinglePostViewPage fetcher={getPublicPost} unauthenticated />
    }
    if (/(feed|friends|tasks|recents|to-do)/i.test(pathname)) {
      typeof window !== 'undefined' &&
        window.location.replace(`/auth/familysearch/login?returnUrl=${encodeURIComponent(window.location.href)}`)
      return <LoadingSpinner />
    }
    if (i18n.language === 'zh' || i18n.language === 'zh-hans') {
      return <ChineseNLIHP />
    }
    return <NLIHP />
  }
  return <LIHP />
}

export default App
